'use client';

/**
 * Consumer Impersonation Banner (Issue #321)
 *
 * Softer language banner for consumers during impersonation sessions.
 * Shows that support is viewing their account in a reassuring way.
 * Consumers cannot end the session - only the actor can.
 */

import SupportIcon from '@mui/icons-material/SupportAgent';
import { Box, iconSizes, Typography, useI18n, useTheme } from '@ops-matrix/design-system-ui';
import { AnimatePresence, motion } from 'framer-motion';
import { useConsumerAuth } from '../../contexts/ConsumerAuthContext';

const BANNER_HEIGHT = 48;

export function ImpersonationBanner(): React.ReactElement | null {
  const { t } = useI18n();
  const theme = useTheme();
  const { isImpersonating, impersonationContext } = useConsumerAuth();

  // Don't render if not impersonating
  if (!isImpersonating || !impersonationContext) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -BANNER_HEIGHT, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -BANNER_HEIGHT, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: theme.zIndex.appBar + 1,
          height: BANNER_HEIGHT,
        }}
      >
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            background: `linear-gradient(90deg, ${theme.palette.info.main}, ${theme.palette.info.light})`,
            color: theme.palette.info.contrastText,
            boxShadow: theme.shadows[2],
          }}
        >
          {/* Centered Info - Consumer-friendly message */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, textAlign: 'center' }}>
            <SupportIcon sx={{ fontSize: iconSizes.base }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                {t('impersonation.consumerBanner.message', {
                  actorEmail: impersonationContext.actorEmail,
                })}
              </Typography>
              {impersonationContext.reason && (
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  {t('impersonation.consumerBanner.reason')}: {impersonationContext.reason}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Info badge */}
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              right: 16,
              opacity: 0.8,
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {t('impersonation.consumerBanner.info')}
          </Typography>
        </Box>
      </motion.div>
    </AnimatePresence>
  );
}

export default ImpersonationBanner;
export { BANNER_HEIGHT };
